import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import TreeControls from './TreeControls/TreeControls';
import TreeView from './TreeView/TreeView';
import { hierarchy as d3Hierarchy } from 'd3-hierarchy';

import styles from './Tree.module.scss';

const isNotNull = attr => {
    return attr !== null && typeof attr !== 'undefined';
};

const Tree = ({ items, ...props }) => {
    const [hierarchy, setHierarchy] = useState(items);
    const [openAll, setOpenAll] = useState(false);

    useEffect(() => {
        initItems(items);
    }, [items]);

    const isDisabled = node => {
        const descendants = node.descendants();
        descendants.shift();
        if (descendants.length === 0) {
            return Boolean(node.data?.disabled);
        }
        return descendants.every(descendant => Boolean(descendant.data?.disabled));
    };

    const isIndeterminate = node => {
        const descendants = node.descendants();
        if (descendants.length === 0) {
            return Boolean(node.data.indeterminate);
        }
        const updatable = descendants.filter(descendant => Boolean(!descendant.data.disabled));
        const checked = updatable.filter(descendant => Boolean(descendant.data.checked));
        return checked.length !== updatable.length && checked.length !== 0;
    };

    const isChecked = node => {
        const leaves = node.leaves();
        if (leaves.length === 0) {
            return Boolean(node.data.checked);
        }
        const updatable = leaves.filter(leaf => Boolean(!leaf.data.disabled));
        const checked = updatable.filter(leaf => Boolean(leaf.data.checked));
        return checked.length > 0;
    };

    const initItems = items => {
        const newHierarchy = items.copy();

        newHierarchy.each(node => {
            node.data = {
                ...node?.data,
                open: Boolean(node?.data?.open),
                checked: isNotNull(node?.data?.checked) ? node?.data?.checked : true,
                disabled: isNotNull(node?.data?.disabled) ? node?.data?.disabled : false,
                indeterminate: false
            };
        });

        newHierarchy.sort((a, b) => a?.data?.displayOrder - b?.data?.displayOrder);

        newHierarchy.eachAfter(node => {
            if (node?.depth > 0) {
                const ancestors = node.ancestors() || [];
                ancestors.shift();
                if (ancestors.length) {
                    node.data.disabled = isDisabled(node);
                    node.data.indeterminate = isIndeterminate(node);
                    node.data.onChange(node);
                }
            }
        });

        setHierarchy(newHierarchy);
    };

    const handleNodeOnChange = (node) => {
        if (node?.depth > 0 && node?.data?.onChange) {
            node?.data?.onChange(node);
        }
    };

    const handleOnChange = (evt) => {
        const { id, checked } = evt;
        const newHierarchy = hierarchy.copy();
        const node = newHierarchy.find(node => node.data.id === id);

        const descendants = node.descendants() || [];
        descendants.forEach(descendant => {
            if (!descendant.data.disabled && descendant?.depth > 0) {
                descendant.data.checked = checked;
            }
            if (!descendant.data.disabled && descendant?.depth > 0 && checked) {
                descendant.data.indeterminate = false;
            }
        });

        const ancestors = node.ancestors() || [];
        ancestors.shift();

        ancestors.forEach(ancestor => {
            if (!ancestor.data.disabled) {
                ancestor.data.checked = isChecked(ancestor);
                ancestor.data.indeterminate = isIndeterminate(ancestor);
            }
        });

        newHierarchy.eachAfter(handleNodeOnChange);

        setHierarchy(newHierarchy);
    };

    const handleOpenAll = () => {
        const open = !openAll;
        const newHierarchy = hierarchy.copy();
        newHierarchy.eachAfter(node => {
            const ancestors = node.ancestors() || [];
            ancestors.shift();
            if (ancestors.length) {
                node.data.open = open;
                node.data.onChange(node);
            }
        });
        setOpenAll(open);
        setHierarchy(newHierarchy);
    };

    const handleOnOpen = evt => {
        const { id, open } = evt;
        const newHierarchy = hierarchy.copy();
        const node = newHierarchy.find(node => node.data.id === id);
        if (node) {
            node.data.open = open;
        }
        if (open) {
            setOpenAll(open);
        } else {
            const descendants = newHierarchy.descendants() || [];
            descendants.shift();
            if (descendants.length) {
                const descendantsOpen = descendants.filter(descendant => descendant.data.open).length > 0;
                setOpenAll(descendantsOpen);
            }
        }
        setHierarchy(newHierarchy);
    };

    const toggleAllNodesCheckedStatus = () => {
        const newHierarchy = hierarchy.copy();
        const previousCheckedState = hierarchy.data.checked;
        newHierarchy.each(node => {
            if (!node.data.disabled) {
                node.data.checked = !previousCheckedState;
                node.data.indeterminate = false;
                handleNodeOnChange(node);
            }
        });

        setHierarchy(newHierarchy);
    };

    return <div className={styles.container} data-testid={props['data-testid']}>
        <TreeControls
            openAll={openAll}
            onOpenAll={handleOpenAll}
            toggleAllLayers={toggleAllNodesCheckedStatus}
            isAllNodesChecked={hierarchy.data.checked}
            indeterminate={hierarchy.data.indeterminate}/>
        <TreeView
            hierarchy={hierarchy}
            onChange={handleOnChange}
            onOpen={handleOnOpen}
        />
    </div>;
};

Tree.propTypes = {
    items: PropTypes.instanceOf(d3Hierarchy).isRequired,
    'data-testid': PropTypes.string
};

Tree.defaultProps = {};

export default Tree;
